// Decision table for how UserIcon should paint a user avatar.
//
// Background: the API embeds direct CDN avatar URLs (`avatar_urls`) on fresh
// user payloads. Older *cached* Answer payloads predate that field, so their
// embedded user reaches UserIcon without `avatar_urls`. The legacy fallback for
// that case hits `GET /api/v1/users/:id/avatar`, which 302-redirects — one
// request per icon (the avatar N+1 seen on the public stream).
//
// A background bulk hydration (`queueUserAvatarHydration`) backfills the missing
// `avatar_urls` within tens of ms. So when we're only *temporarily* missing the
// CDN url, we show initials for a short grace window instead of
// firing the redirect. Only if hydration settles without producing a CDN url
// do we fall back to the redirect endpoint. A failed image is terminal and
// renders initials; retrying the same broken source creates a render loop.

export const AVATAR_HYDRATION_GRACE_MS = 1000

export type AvatarRenderMode = 'cdn' | 'awaiting-hydration' | 'redirect' | 'initials'

export function resolveAvatarRenderMode({
    hasAvatarUrls,
    hasAvatarError,
    userId,
    hydrationSettledUserId,
}: {
    hasAvatarUrls: boolean
    hasAvatarError: boolean
    userId: string | number | null | undefined
    hydrationSettledUserId: string | null
}): AvatarRenderMode {
    // A prior CDN/redirect error is terminal for this avatar version.
    if (hasAvatarError) return 'initials'
    // Direct CDN url available — render it, no redirect and no wait.
    if (hasAvatarUrls) return 'cdn'
    // avatar_urls missing but bulk hydration is still within its grace window:
    // show initials rather than firing the per-user /avatar 302.
    // Tie settled state to the user it belongs to. Virtualized/reused icon
    // instances can receive new props before the effect resets local state;
    // a previous user's elapsed timer must not unlock this user's redirect.
    if (!userId || String(userId) !== hydrationSettledUserId) return 'awaiting-hydration'
    // Grace elapsed without a CDN url — fall back to the redirect endpoint.
    return 'redirect'
}
